import { Box, Flex, SimpleGrid, Text } from '@chakra-ui/react';
import PropTypes from 'prop-types';
import { fmtMoneyDebug } from '../calc/formatters';

export function formatFprNumber(value, suffix = '') {
  if (value == null || !Number.isFinite(value)) {
    return '—';
  }
  const rounded = Number(value.toFixed(2));
  if (Math.abs(rounded - Math.round(rounded)) < 1e-9) {
    return `${Math.round(rounded)}${suffix}`;
  }
  return `${value.toFixed(2)}${suffix}`;
}

function formatPct(value) {
  return formatFprNumber(value, '%');
}

function formatStep(value) {
  return formatFprNumber(value);
}

function kpiCell(label, value, detail) {
  return (
    <Box bg="white" p={4}>
      <Text fontSize="xs" textTransform="uppercase" color="gray.500" letterSpacing="0.04em">
        {label}
      </Text>
      <Text fontSize="2xl" fontWeight="700" className="brand" mt={1}>
        {value}
      </Text>
      {detail ? (
        <Text fontSize="xs" color="gray.500" mt={1}>
          {detail}
        </Text>
      ) : null}
    </Box>
  );
}

function cellStyle(row, moneyBackMonth, closedMonth, options = {}) {
  if (closedMonth !== null && row.month === closedMonth) {
    return { background: '#146c2e', color: '#fff' };
  }
  if (moneyBackMonth !== null && row.month === moneyBackMonth) {
    return { background: '#e4f3e8' };
  }
  if (row.accelerated) {
    return { background: '#fbeee6' };
  }
  if (options.showAnchor && row.isAnchor) {
    return { background: '#eef0fe' };
  }
  return undefined;
}

function FprResults({ result }) {
  if (!result) {
    return null;
  }

  const { rows, ledger, infeasible, inputs } = result;
  const closedLabel = ledger.closedMonth == null ? '—' : `M${ledger.closedMonth}`;
  const backLabel = ledger.moneyBackMonth == null ? '—' : `M${ledger.moneyBackMonth}`;

  return (
    <Box className="section-stack" mt={10}>
      {infeasible ? (
        <Box className="card" p={5} borderTopColor="#d03b3b">
          <Text fontWeight="600">These terms cannot be met by a decaying cohort.</Text>
          <Text fontSize="sm" color="gray.600" mt={1}>
            At month {infeasible.month} the contract needs a step of{' '}
            {infeasible.need.toFixed(2)} while the previous step was{' '}
            {infeasible.previousStep.toFixed(2)}.
          </Text>
        </Box>
      ) : null}

      <SimpleGrid columns={{ base: 1, md: 4 }} spacing="1px" bg="#e2e4f0" borderRadius="12px" overflow="hidden">
        {kpiCell('Money back', backLabel, 'First month collected cash covers the funded amount.')}
        {kpiCell('Cohort closes', closedLabel, 'Funded amount plus target monthly XIRR, balance gone.')}
        {kpiCell(
          'Total paid',
          `${ledger.multiple.toFixed(3)}x`,
          `${fmtMoneyDebug(ledger.total, 0)} on ${fmtMoneyDebug(ledger.fundedAmount, 0)} funded.`
        )}
        {kpiCell(
          'Normal take',
          formatPct(inputs.fundingShare * inputs.revenueShare * 100),
          `Accelerated take ${formatPct(inputs.accelerationShare * 100)} of revenue.`
        )}
      </SimpleGrid>

      <Box className="card">
        <Flex className="section-header" align="center">
          <Text fontWeight="600">Worst path (longest time to repay)</Text>
        </Flex>
        <Box overflowX="auto">
          <table className="cohort-sheet-table">
            <thead>
              <tr>
                <th> </th>
                {rows.map((row) => (
                  <th key={row.month}>M{row.month}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Revenue vector</td>
                {rows.map((row) => (
                  <td
                    key={`client-${row.month}`}
                    style={{
                      ...cellStyle(row, ledger.moneyBackMonth, ledger.closedMonth),
                    }}
                  >
                    {formatPct(row.clientRoas)}
                  </td>
                ))}
              </tr>
              <tr>
                <td>MoM</td>
                {rows.map((row) => (
                  <td
                    key={`mom-${row.month}`}
                    style={{
                      ...cellStyle(row, ledger.moneyBackMonth, ledger.closedMonth),
                    }}
                  >
                    {formatStep(row.clientStep)}
                  </td>
                ))}
              </tr>
              <tr>
                <td>Reference revenue ({formatPct(inputs.revenueShare * 100)})</td>
                {rows.map((row) => (
                  <td
                    key={`ref-${row.month}`}
                    title={
                      row.isAnchor && row.fullAnchor != null
                        ? `Anchor ${formatPct(row.fullAnchor)} on reference revenue`
                        : undefined
                    }
                    style={{
                      ...cellStyle(row, ledger.moneyBackMonth, ledger.closedMonth, {
                        showAnchor: true,
                      }),
                    }}
                  >
                    {formatPct(row.referenceRoas)}
                  </td>
                ))}
              </tr>
              <tr>
                <td>MoM reference</td>
                {rows.map((row) => (
                  <td
                    key={`mom-ref-${row.month}`}
                    style={{
                      ...cellStyle(row, ledger.moneyBackMonth, ledger.closedMonth),
                    }}
                  >
                    {formatStep(row.referenceStep)}
                  </td>
                ))}
              </tr>
              <tr>
                <td>Collection</td>
                {rows.map((row) => {
                  const ledgerRow = ledger.rows[row.month] || {};
                  return (
                    <td
                      key={`coll-roas-${row.month}`}
                      style={{
                        ...cellStyle(row, ledger.moneyBackMonth, ledger.closedMonth),
                      }}
                    >
                      {formatPct(ledgerRow.collectionRoas)}
                    </td>
                  );
                })}
              </tr>
              <tr>
                <td>MoM collection</td>
                {rows.map((row) => {
                  const ledgerRow = ledger.rows[row.month] || {};
                  const previousRoas =
                    row.month === 0
                      ? 0
                      : (ledger.rows[row.month - 1] || {}).collectionRoas || 0;
                  const momCollection =
                    (ledgerRow.collectionRoas || 0) - previousRoas;
                  return (
                    <td
                      key={`mom-coll-${row.month}`}
                      style={{
                        ...cellStyle(row, ledger.moneyBackMonth, ledger.closedMonth),
                      }}
                    >
                      {formatStep(momCollection)}
                    </td>
                  );
                })}
              </tr>
              <tr>
                <td>Collection $</td>
                {rows.map((row) => {
                  const ledgerRow = ledger.rows[row.month] || {};
                  return (
                    <td
                      key={`coll-${row.month}`}
                      style={{
                        ...cellStyle(row, ledger.moneyBackMonth, ledger.closedMonth),
                      }}
                    >
                      {ledgerRow.pay ? fmtMoneyDebug(ledgerRow.pay, 0) : '—'}
                    </td>
                  );
                })}
              </tr>
              <tr>
                <td>Cumulative collection</td>
                {rows.map((row) => {
                  const ledgerRow = ledger.rows[row.month] || {};
                  return (
                    <td
                      key={`cum-coll-${row.month}`}
                      style={{
                        ...cellStyle(row, ledger.moneyBackMonth, ledger.closedMonth),
                      }}
                    >
                      {Number.isFinite(ledgerRow.cumulativeCollected)
                        ? fmtMoneyDebug(ledgerRow.cumulativeCollected, 0)
                        : '—'}
                    </td>
                  );
                })}
              </tr>
              <tr>
                <td>Treisar ROAS</td>
                {rows.map((row) => {
                  const ledgerRow = ledger.rows[row.month] || {};
                  return (
                    <td
                      key={`treisar-${row.month}`}
                      style={{
                        ...cellStyle(row, ledger.moneyBackMonth, ledger.closedMonth),
                      }}
                    >
                      {formatPct(ledgerRow.treisarRoas)}
                    </td>
                  );
                })}
              </tr>
              <tr>
                <td>MoM Treisar</td>
                {rows.map((row) => {
                  const ledgerRow = ledger.rows[row.month] || {};
                  const previousTreisar =
                    row.month === 0
                      ? 0
                      : (ledger.rows[row.month - 1] || {}).treisarRoas || 0;
                  const momTreisar = (ledgerRow.treisarRoas || 0) - previousTreisar;
                  return (
                    <td
                      key={`mom-treisar-${row.month}`}
                      style={{
                        ...cellStyle(row, ledger.moneyBackMonth, ledger.closedMonth),
                      }}
                    >
                      {formatStep(momTreisar)}
                    </td>
                  );
                })}
              </tr>
            </tbody>
          </table>
        </Box>
        <Flex px={5} py={3} gap={5} wrap="wrap" fontSize="xs" color="gray.500">
          <Text>Purple = anchor check on the reference revenue vector</Text>
          <Text>Orange = acceleration (anchor miss until the next anchor, or under MoM)</Text>
          <Text>Light green = funded amount is whole</Text>
          <Text>Dark green = funded amount plus XIRR is repaid</Text>
        </Flex>
      </Box>

      <Box className="card" mt={4}>
        <Flex className="section-header" align="center">
          <Text fontWeight="600">Why this path</Text>
        </Flex>
        <Box p={5}>
          {(Array.isArray(result.explanation)
            ? result.explanation
            : result.explanation
              ? [result.explanation]
              : []
          ).map((line) => (
            <Text key={line} fontSize="sm" color="gray.700" mb={2}>
              {line}
            </Text>
          ))}
        </Box>
      </Box>
    </Box>
  );
}

FprResults.propTypes = {
  result: PropTypes.shape({
    rows: PropTypes.array.isRequired,
    ledger: PropTypes.shape({
      rows: PropTypes.array.isRequired,
      moneyBackMonth: PropTypes.number,
      closedMonth: PropTypes.number,
      total: PropTypes.number.isRequired,
      multiple: PropTypes.number.isRequired,
      fundedAmount: PropTypes.number.isRequired,
    }).isRequired,
    infeasible: PropTypes.shape({
      month: PropTypes.number.isRequired,
      need: PropTypes.number.isRequired,
      previousStep: PropTypes.number.isRequired,
    }),
    inputs: PropTypes.shape({
      uaAmount: PropTypes.number.isRequired,
      fundingShare: PropTypes.number.isRequired,
      revenueShare: PropTypes.number.isRequired,
      accelerationShare: PropTypes.number.isRequired,
    }).isRequired,
    explanation: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.arrayOf(PropTypes.string),
    ]),
    strategy: PropTypes.shape({
      missAnchorMonths: PropTypes.arrayOf(PropTypes.number),
      momUsesFloor: PropTypes.bool,
    }),
  }),
};

export default FprResults;
